/**
 * Professional Profile Hooks
 * Hooks for managing professional profile information
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE_URL } from '@shared/lib/api/client'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import { tokenStorage } from '@shared/lib/storage'
import type { Professional } from '@shared/types'

// ============================================================================
// Query Keys
// ============================================================================

export const professionalProfileKeys = {
  all: ['professionalProfile'] as const,
  byUserId: (userId: string) => [...professionalProfileKeys.all, 'byUser', userId] as const,
}

// ============================================================================
// Types
// ============================================================================

export interface CreateProfessionalData {
  professionalName: string
  bio?: string
  professionId: string
  cityId: string
  gender?: 'male' | 'female' | ''
  contactPhone: string
  profilePictureUrl?: string
  instagramUrl?: string
  facebookUrl?: string
  googleMapsUrl?: string
}

export interface UpdateProfessionalData extends Partial<CreateProfessionalData> {
  isActive?: boolean
}

interface BackendProfessionalDTO {
  id: string
  userId?: string
  name?: string
  picture?: string
  city?: Array<{ id: string; name?: string }>
  cityId?: string
  professions?: Array<{ id: string; name?: string }>
  bio?: string
  gender?: 'male' | 'female' | null
  socials?: { instagram?: string; facebook?: string; googleMaps?: string }
  contact?: string
  activeAccount?: boolean
  goodProfessionalRate?: number
  comments?: number
  createdAt?: string
  updatedAt?: string
}

interface UploadResponse {
  success: boolean
  data?: unknown
  message?: { error?: string }
}

interface ActivateResponse {
  professional?: BackendProfessionalDTO
  token?: string
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get professional profile for current user
 * Uses the current authenticated user's profile endpoint
 * Backend returns: { professional: { ... } } structure
 */
export function useProfessionalProfileByUserId(userId: string) {
  return useQuery({
    queryKey: professionalProfileKeys.byUserId(userId),
    queryFn: async () => {
      // Backend returns: { professional: {...} }
      const response = await apiClient.get<{ professional: BackendProfessionalDTO | null }>(
        API_ENDPOINTS.professionals.currentProfile
      )

      // Extract the professional object from DTO
      const prof = response.professional

      // If no professional profile exists, return undefined
      if (!prof) {
        return undefined
      }

      // Backend DTO structure:
      // - id: string
      // - name: string (professionalName)
      // - picture: string
      // - city: Array<{ id, name }>
      // - professions: Array<{ id, name }>
      // - bio: string
      // - socials: { instagram, facebook, googleMaps }
      // - contact: string (if contact flag is true)
      // - activeAccount: boolean

      // Map backend fields to frontend Professional type
      return {
        id: prof.id,
        userId: prof.userId,
        professionalName: prof.name,
        bio: prof.bio,
        professionId: prof.professions?.[0]?.id || '', // Get first profession ID
        cityId: prof.city?.[0]?.id || prof.cityId || '', // Try array first, then direct field
        gender: prof.gender ?? null,
        contactPhone: prof.contact || '',
        profilePictureUrl: prof.picture,
        instagramUrl: prof.socials?.instagram,
        facebookUrl: prof.socials?.facebook,
        googleMapsUrl: prof.socials?.googleMaps,
        isActive: prof.activeAccount ?? true,
        averageRating: prof.goodProfessionalRate || 0,
        totalReviews: prof.comments || 0,
        createdAt: prof.createdAt || new Date().toISOString(),
        updatedAt: prof.updatedAt || new Date().toISOString(),
      } as Professional
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry if profile doesn't exist (user hasn't created one yet)
  })
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create new professional profile
 * Backend expects: POST /api/professionals with form data
 */
export function useCreateProfessionalProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateProfessionalData) => {
      return apiClient.post<Professional>(API_ENDPOINTS.professionals.create, {
        professionalName: data.professionalName,
        bio: data.bio,
        professions: data.professionId ? [data.professionId] : [],
        city: data.cityId ? [data.cityId] : [],
        gender: data.gender || undefined,
        professionalContact: data.contactPhone,
        socials: {
          instagram: data.instagramUrl,
          facebook: data.facebookUrl,
          googleMaps: data.googleMapsUrl,
        },
      })
    },
    onSuccess: () => {
      // Invalidate professional profile queries
      void queryClient.invalidateQueries({ queryKey: professionalProfileKeys.all })
    },
  })
}

/**
 * Update professional profile
 * Backend endpoint: POST /api/professionals/updateProfessionalInformation
 */
export function useUpdateProfessionalProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateProfessionalData) => {
      return apiClient.post<Professional>(API_ENDPOINTS.professionals.updateCurrent, {
        professionalName: data.professionalName,
        bio: data.bio,
        professions: data.professionId ? [data.professionId] : [],
        professionalCity: data.cityId ? [data.cityId] : [],
        gender: data.gender || undefined,
        professionalContact: data.contactPhone,
        socials: {
          instagram: data.instagramUrl,
          facebook: data.facebookUrl,
          googleMaps: data.googleMapsUrl,
        },
      })
    },
    onSuccess: () => {
      // Invalidate professional profile queries
      void queryClient.invalidateQueries({ queryKey: professionalProfileKeys.all })
    },
  })
}

/**
 * Activate professional profile
 * Backend endpoint: POST /api/professionals
 * Returns: { professional, token } - new JWT token with updated professionalId
 */
export function useActivateProfessionalProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      return apiClient.post<ActivateResponse>(API_ENDPOINTS.professionals.create, {})
    },
    onSuccess: async (response) => {
      // If backend returns a new token, store it
      if (response?.token) {
        tokenStorage.setToken(response.token)
      }

      // Invalidate and refetch both professional and user profile queries
      // User profile needs to be invalidated because the user object is updated with professional ID
      await queryClient.invalidateQueries({ queryKey: professionalProfileKeys.all, refetchType: 'all' })
      await queryClient.invalidateQueries({ queryKey: ['userProfile'], refetchType: 'all' })
    },
  })
}

/**
 * Deactivate professional profile
 * Backend endpoint: DELETE /api/professionals/deactivateAccount
 */
export function useDeactivateProfessionalProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      return apiClient.delete(API_ENDPOINTS.professionals.deactivateCurrent, { requiresAuth: true })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: professionalProfileKeys.all })
    },
  })
}

/**
 * Upload professional profile picture
 * Backend endpoint: POST /api/professionals/updateProfessionalInformation (with file)
 */
export function useUploadProfessionalProfilePicture() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('profilePicture', file)

      const token = tokenStorage.getToken()
      const headers: Record<string, string> = {}

      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Use fetch directly for FormData with file
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.professionals.updateCurrent}`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData,
        }
      )

      if (!response.ok) {
        throw new Error('Failed to upload professional picture')
      }

      // Handle 204 No Content response (success with no body)
      if (response.status === 204) {
        return null
      }

      const json = (await response.json()) as UploadResponse
      if (!json.success) {
        throw new Error(json.message?.error || 'Failed to upload professional picture')
      }

      return json.data
    },
    onSuccess: () => {
      // Invalidate professional profile to refetch with new picture
      void queryClient.invalidateQueries({ queryKey: professionalProfileKeys.all })
    },
  })
}

/**
 * Remove professional profile picture
 * Sends removePicture flag to backend
 */
export function useRemoveProfessionalProfilePicture() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const formData = new FormData()
      formData.append('removePicture', 'true')

      const token = tokenStorage.getToken()
      const headers: Record<string, string> = {}

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.professionals.updateCurrent}`,
        {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData,
        }
      )

      if (!response.ok) {
        throw new Error('Failed to remove professional picture')
      }

      if (response.status === 204) {
        return null
      }

      const json = (await response.json()) as UploadResponse
      if (!json.success) {
        throw new Error(json.message?.error || 'Failed to remove professional picture')
      }

      return json.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: professionalProfileKeys.all })
    },
  })
}
